import React, { useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Search, RefreshCw, ShieldCheck, Users, CreditCard, UserCheck } from "lucide-react";
import { readAdminUsers, readAdminSubscriptions, updateAdminUser } from "../api/users";
import styles from "./UsersPage.module.css";
import { useAuth } from "../../../context/AuthContext";

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [access, setAccess] = useState("all");
  const [role, setRole] = useState("all");
  const [loading, setLoading] = useState(true);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionsWarning, setSubscriptionsWarning] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError(""); setSubscriptionsWarning("");
    try {
      const users = await readAdminUsers();
      setRows(users.map(u => ({ ...u, subscription: null })));
      setLoading(false);

      setSubscriptionsLoading(true);
      try {
        const subs = await readAdminSubscriptions({ timeoutMs: 8000 });
        const byEmail = new Map(subs.map(s => [String(s.email || "").toLowerCase(), s]));
        setRows(users.map(u => ({ ...u, subscription: byEmail.get(String(u.email || "").toLowerCase()) || null })));
      } catch (e) {
        setSubscriptionsWarning("Subscription service is temporarily slow. User access data is available; subscription status will be retried on refresh.");
      } finally {
        setSubscriptionsLoading(false);
      }
    } catch(e) {
      setError(e.message || "Unable to load users");
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const paying = rows.filter(r => r.subscription?.paying).length;
    const active = rows.filter(r => r.enabled).length;
    const admins = rows.filter(r => String(r.role).toUpperCase() === "ADMIN").length;
    return { total: rows.length, paying, free: rows.length-paying, active, admins };
  }, [rows]);

  const filtered = useMemo(() => rows.filter(r => {
    const hit = `${r.displayName || ""} ${r.email || ""}`.toLowerCase().includes(q.trim().toLowerCase());
    const paying = !!r.subscription?.paying;
    const statusHit = status === "all" || (status === "paying" ? paying : !paying);
    const accessHit = access === "all" || (access === "active" ? r.enabled : !r.enabled);
    const roleHit = role === "all" || String(r.role || "").toUpperCase() === role;
    return hit && statusHit && accessHit && roleHit;
  }), [rows,q,status,access,role]);

  const patch = async (row, body) => {
    setBusy(row.id);
    try { await updateAdminUser(row.id, body); await load(); }
    catch (e) { setError(e.message || "Unable to update user permissions"); }
    finally { setBusy(null); }
  };

  const subscriptionData = [{name:"Paying",value:stats.paying},{name:"Not paying",value:stats.free}];
  const accessData = [{name:"Active",value:stats.active},{name:"Inactive",value:stats.total-stats.active}];

  return <div className={styles.page}>
    <div className={styles.heading}><div><h1>Users & subscriptions</h1><p>Access, roles and subscription health in one place.</p></div>
      <button className={styles.refresh} onClick={load}><RefreshCw size={16}/> Refresh</button></div>

    <div className={styles.cards}>
      <div className={styles.card}><Users/><div><strong>{stats.total}</strong><span>Total users</span></div></div>
      <div className={styles.card}><CreditCard/><div><strong>{stats.paying}</strong><span>Paying subscribers</span></div></div>
      <div className={styles.card}><UserCheck/><div><strong>{stats.active}</strong><span>Active access</span></div></div>
      <div className={styles.card}><ShieldCheck/><div><strong>{stats.admins}</strong><span>Administrators</span></div></div>
    </div>

    <div className={styles.charts}>
      <div className={styles.panel}><h3>Subscription mix</h3><ResponsiveContainer width="100%" height={220}><PieChart><Pie data={subscriptionData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={3}><Cell/><Cell/></Pie><Tooltip/></PieChart></ResponsiveContainer></div>
      <div className={styles.panel}><h3>Access status</h3><ResponsiveContainer width="100%" height={220}><BarChart data={accessData}><CartesianGrid strokeDasharray="3 3"/><XAxis dataKey="name"/><YAxis allowDecimals={false}/><Tooltip/><Bar dataKey="value"/></BarChart></ResponsiveContainer></div>
    </div>

    <div className={styles.toolbar}>
      <label className={styles.search}><Search size={17}/><input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search name or email"/></label>
      <select value={status} onChange={e=>setStatus(e.target.value)}><option value="all">All subscriptions</option><option value="paying">Paying</option><option value="free">Not paying</option></select>
      <select value={access} onChange={e=>setAccess(e.target.value)}><option value="all">All access</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
      <select value={role} onChange={e=>setRole(e.target.value)}><option value="all">All roles</option><option value="USER">User</option><option value="ADMIN">Administrator</option></select>
      <button className={styles.clear} onClick={()=>{setQ("");setStatus("all");setAccess("all");setRole("all")}}>Clear filters</button>
    </div>

    {error && <div className={styles.error}>{error}</div>}
    {subscriptionsWarning && <div className={styles.warning}>{subscriptionsWarning}</div>}
    {subscriptionsLoading && !loading && <div className={styles.info}>Users loaded. Loading subscription status…</div>}
    {loading ? <div className={styles.empty}>Loading users…</div> :
    <div className={styles.tableWrap}><table><thead><tr><th>User</th><th>Role</th><th>Access</th><th>Subscription</th><th>Plan</th><th>Next billing</th></tr></thead>
      <tbody>{filtered.map(r=><tr key={r.id}><td><div className={styles.person}><div className={styles.avatar}>{(r.displayName||r.email||"?").slice(0,1).toUpperCase()}</div><div><b>{r.displayName||"Unnamed user"}</b><span>{r.email}</span></div></div></td>
      <td><select disabled={busy===r.id || String(currentUser?.id)===String(r.id)} value={(r.role||"user").toUpperCase()} onChange={e=>{ const next=e.target.value.toLowerCase(); if(window.confirm(`Change ${r.email} role to ${next}?`)) patch(r,{role:next}); }}><option value="USER">User</option><option value="ADMIN">Administrator</option></select></td>
      <td><button disabled={busy===r.id || String(currentUser?.id)===String(r.id)} className={r.enabled?styles.activeBtn:styles.inactiveBtn} onClick={()=>patch(r,{enabled:!r.enabled})}>{r.enabled?"Active":"Inactive"}</button></td>
      <td><span className={r.subscription?.paying?styles.paid:styles.free}>{r.subscription?.paying?"Paying":"Not paying"}</span></td>
      <td>{r.subscription?.planId||"—"}</td><td>{r.subscription?.nextBillingAt?new Date(r.subscription.nextBillingAt).toLocaleDateString():"—"}</td></tr>)}</tbody></table>
      {!filtered.length && <div className={styles.empty}>No users match these filters.</div>}</div>}
  </div>;
}
